import {
  ExternalCalendarEvent,
  ID,
  overdueSectionID,
  Task,
  Timestamp,
} from '@griply/types';
import { isBetween, parseDateKey } from '@griply/utils';
import { ItemInstance } from '@headless-tree/core';
import Typography from '@mui/material/Typography';
import {
  differenceInDays,
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  getDate,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'shared/assets/icons';
import { RescheduleButton } from 'shared/components/connected/reschedule-button';
import { ExternalEventsList } from 'shared/components/ui/external-events-list';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { ListSectionDateHeader } from 'shared/components/ui/list-section-date-header';
import { ListSectionMonthHeader } from 'shared/components/ui/list-section-month-header';
import { ListSectionYearHeader } from 'shared/components/ui/list-section-year-header';
import { InternalTreeItem } from 'shared/components/ui/virtualized-tree-list';
import { TimeFormatOptions } from 'shared/types/date-format-options';

import {
  upcomingMonthPrefix,
  upcomingRestOfMonthPrefix,
  upcomingYearPrefix,
} from '../../hooks/use-upcoming-deadline-tree';
import * as DeadlineStyled from '../deadline-header/deadline-header.style';
import * as Styled from './upcoming-deadline-header.style';

enum SectionType {
  Day = 'day',
  RestOfMonth = 'rest-of-month',
  Month = 'month',
  Year = 'year',
  Overdue = 'overdue',
}

type ParsedSection = {
  type: SectionType;
  date: Timestamp | undefined;
  startDate: Timestamp | undefined;
  endDate: Timestamp | undefined;
};

const parseSectionId = (sectionId: ID): ParsedSection => {
  if (sectionId === overdueSectionID) {
    return {
      type: SectionType.Overdue,
      date: undefined,
      startDate: undefined,
      endDate: undefined,
    };
  }

  if (sectionId.startsWith(upcomingRestOfMonthPrefix)) {
    const dateStr = sectionId.slice(upcomingRestOfMonthPrefix.length);
    const parsed = parseDateKey(dateStr);
    if (isNaN(parsed.getTime())) {
      return {
        type: SectionType.RestOfMonth,
        date: undefined,
        startDate: undefined,
        endDate: undefined,
      };
    }
    return {
      type: SectionType.RestOfMonth,
      date: parsed,
      startDate: startOfDay(parsed),
      endDate: endOfMonth(parsed),
    };
  }

  if (sectionId.startsWith(upcomingMonthPrefix)) {
    const dateStr = sectionId.slice(upcomingMonthPrefix.length);
    const parsed = parseDateKey(dateStr);
    if (isNaN(parsed.getTime())) {
      return {
        type: SectionType.Month,
        date: undefined,
        startDate: undefined,
        endDate: undefined,
      };
    }
    return {
      type: SectionType.Month,
      date: parsed,
      startDate: startOfMonth(parsed),
      endDate: endOfMonth(parsed),
    };
  }

  if (sectionId.startsWith(upcomingYearPrefix)) {
    const dateStr = sectionId.slice(upcomingYearPrefix.length);
    const parsed = parseDateKey(dateStr);
    if (isNaN(parsed.getTime())) {
      return {
        type: SectionType.Year,
        date: undefined,
        startDate: undefined,
        endDate: undefined,
      };
    }
    return {
      type: SectionType.Year,
      date: parsed,
      startDate: startOfMonth(parsed),
      endDate: endOfYear(parsed),
    };
  }

  const parsed = parseDateKey(sectionId);
  if (isNaN(parsed.getTime())) {
    return {
      type: SectionType.Day,
      date: undefined,
      startDate: undefined,
      endDate: undefined,
    };
  }
  return {
    type: SectionType.Day,
    date: parsed,
    startDate: startOfDay(parsed),
    endDate: endOfDay(parsed),
  };
};

const parseDateAsLocalMidnight = (dateString: string) => {
  const d = new Date(dateString);
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    0,
    0,
    0,
    0,
  );
};

const filterEventsForSection = (
  events: ExternalCalendarEvent[],
  startDate: Timestamp,
  endDate: Timestamp,
): ExternalCalendarEvent[] =>
  events.filter((event) => {
    const eventDate = event.start.date
      ? parseDateAsLocalMidnight(event.start.date)
      : new Date(event.start.dateTime!);
    return isBetween(eventDate, startDate, endDate);
  });

export type UpcomingDeadlineHeaderProps = {
  item: ItemInstance<InternalTreeItem>;
  denseFormat?: boolean;
  overdueTasks?: Task[];
  externalEvents?: ExternalCalendarEvent[];
  timeFormat?: TimeFormatOptions;
};

export const UpcomingDeadlineHeader: React.FC<UpcomingDeadlineHeaderProps> = ({
  item,
  denseFormat,
  overdueTasks,
  externalEvents,
  timeFormat,
}) => {
  const { t } = useTranslation();
  const sectionId = item.getItemData()?.sourceId;
  const isOverdue = sectionId === overdueSectionID;

  const toggleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    item.isExpanded() ? item.collapse() : item.expand();
  };

  const section = useMemo(() => {
    if (!sectionId) {
      return {
        type: SectionType.Day,
        date: undefined,
        startDate: undefined,
        endDate: undefined,
      };
    }
    return parseSectionId(sectionId);
  }, [sectionId]);

  const sectionEvents = useMemo(() => {
    if (!externalEvents?.length || !section.startDate || !section.endDate) {
      return [];
    }
    return filterEventsForSection(
      externalEvents,
      section.startDate,
      section.endDate,
    );
  }, [externalEvents, section.startDate, section.endDate]);

  if (!sectionId) {
    return null;
  }

  const renderHeader = () => {
    if (section.type === SectionType.Overdue) {
      return (
        <Typography variant="h6" component="span">
          {t('general.sections.overdue.title')}
        </Typography>
      );
    }

    if (section.type === SectionType.Day && section.date) {
      return <ListSectionDateHeader date={section.date} />;
    }

    if (section.type === SectionType.RestOfMonth && section.date) {
      const romStart = startOfDay(section.date);
      const romEnd = endOfMonth(section.date);
      const oneDayLeft = differenceInDays(romEnd, romStart) <= 0;

      return (
        <Styled.RestOfMonthContainer>
          <Typography variant="h6" component="span">
            {t('general.labels.rest-of-month.title', {
              month: format(romStart, 'LLLL'),
            })}
          </Typography>
          <Typography variant="h6" component="span" color="text.secondary">
            {oneDayLeft
              ? format(romStart, 'd MMM')
              : t('general.labels.rest-of-days.title', {
                  start: getDate(romStart),
                  end: getDate(romEnd),
                })}
          </Typography>
        </Styled.RestOfMonthContainer>
      );
    }

    if (section.type === SectionType.Month && section.date) {
      return <ListSectionMonthHeader date={section.date} />;
    }

    if (section.type === SectionType.Year && section.date) {
      return <ListSectionYearHeader date={section.date} />;
    }

    return (
      <Typography variant="h6" component="span">
        {item.getItemData().name}
      </Typography>
    );
  };

  return (
    <Styled.SectionContainer>
      <DeadlineStyled.HeaderContainer $denseFormat={denseFormat}>
        {renderHeader()}

        {isOverdue && (
          <Styled.OverdueActions>
            {!!overdueTasks?.length && (
              <RescheduleButton tasks={overdueTasks} />
            )}
            <IconButton
              icon={ChevronLeft}
              onClick={toggleOpen}
              rotation={item.isExpanded() ? -90 : 0}
              color={Colors.Default}
            />
          </Styled.OverdueActions>
        )}
      </DeadlineStyled.HeaderContainer>

      {!!sectionEvents.length && timeFormat && (
        <Styled.ExternalEventsContainer>
          <ExternalEventsList
            events={sectionEvents}
            timeFormat={timeFormat}
            show
          />
        </Styled.ExternalEventsContainer>
      )}
    </Styled.SectionContainer>
  );
};
